import ApprovalActionPanel, {APPROVE} from "./ApprovalActionPanel.vue";
import checkApi from "@/api/dz-project/project/checkApi";
import Util from "@/util/util";

/** 任务状态：0待处理 100处理中 200已处理 300已取消 */
const TASK_PENDING = 0;
const TASK_PROCESSING = 100;

/**
 * 审批详情页的取数逻辑。
 *
 * 浏览器端与移动端是两个页面（布局差得远，各自写模板），但「任务从哪来、怎么认出我的那条、
 * 该用哪种操作模式」完全一样，所以抽在这里。两页各写一份的话，改一处漏一处，
 * 表现就是「电脑上能批、手机上点不动」这类只在某一个端才出现的问题。
 *
 * 使用方只需要提供模板，并按需覆盖 fallbackPath（返回兜底地址）。
 */
export default {
  components: {ApprovalActionPanel},
  data() {
    return {
      loading: false,
      loadError: "",
      task: null,
      mode: APPROVE.DECIDE,
      /** 没有浏览历史时的兜底去处，两个端各自不同 */
      fallbackPath: "/welcome",
      /** 单据字段列数，宽屏 2、窄屏 1 */
      formColumns: 2,
    };
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      const query = this.route.query || {};
      const instanceId = query.instanceId || query.id;
      if (!instanceId) {
        this.loadError = "缺少单据参数，无法打开审批";
        return;
      }
      this.loading = true;
      try {
        const detail = await checkApi.findInstanceDetail(instanceId);
        if (!detail) {
          this.loadError = "未找到该单据，或你没有查看权限";
          return;
        }
        const mine = await this.pickTask(detail.taskList, query.taskId);
        if (!mine) {
          this.task = null;
          return;
        }
        this.task = this.buildTask(mine, detail);
        this.mode = this.resolveMode(mine);
      } catch (e) {
        this.loadError = "单据加载失败，请稍后重试";
      } finally {
        this.loading = false;
      }
    },
    /**
     * 找出要操作的那条任务。
     *
     * 列表入口带着 taskId 来，直接用 —— 同一个实例上我可能既有历史任务、
     * 又有当前待办，靠猜会把「查看旧任务」点成「处理新任务」。
     *
     * 企业微信消息链接只有 instanceId，退化为按人推导：
     * 优先未处理的（拿错的话按钮会点在已结束的任务上），没有才回看我最近处理过的那条。
     */
    async pickTask(taskList, taskId) {
      const all = taskList || [];
      if (taskId) {
        const exact = all.find(t => t && t.id === taskId);
        if (exact) {
          return exact;
        }
        // 实例详情的 taskList 只放当前待办，已处理的任务不在里面。
        // 不单独查一次的话，「查看已处理」会一路退化成「没有待办」，
        // 明明列表里点得到、点进去却说没有需要处理的 —— 就是这个原因。
        try {
          const single = await checkApi.findTask(taskId);
          if (single) {
            return single;
          }
        } catch (e) {
          // 查不到就继续走下面的推导，不因为一次查询失败直接把页面打死
        }
      }
      const me = await Util.getUserInfo();
      const mine = all.filter(t => t && t.assigneeId === (me && me.id));
      if (!mine.length) {
        return null;
      }
      const pending = mine.filter(t =>
        t.taskStatus === TASK_PENDING || t.taskStatus === TASK_PROCESSING);
      if (pending.length) {
        return pending[0];
      }
      return mine.sort((a, b) => String(b.updatedTime || "").localeCompare(String(a.updatedTime || "")))[0];
    },
    /** 任务信息优先取自身，缺少的展示字段从实例上补 */
    buildTask(task, detail) {
      return {
        ...task,
        bizTitle: task.bizTitle || detail.bizTitle,
        businessName: task.businessName || detail.businessName,
        businessType: task.businessType || detail.businessType,
        startUserName: task.startUserName || detail.startUserName,
        instanceId: task.instanceId || detail.id,
      };
    },
    /**
     * 决定操作模式。
     *
     * 与审批中心列表给的入口一致：已处理只能看、抄送只能确认、未处理的审批给同意/驳回。
     * 由任务自身推导而不是由调用方传进来，避免同一件事有两处判断、哪天改漏一边。
     */
    resolveMode(task) {
      const done = task.taskStatus === 200 || task.taskStatus === 300;
      if (done) {
        return APPROVE.VIEW;
      }
      if (task.taskTypeName === "抄送") {
        return APPROVE.CONFIRM;
      }
      return APPROVE.DECIDE;
    },
    /**
     * 返回上一页。
     *
     * 用 back 而不是固定跳某个地址：来源页可能是审批中心、也可能是企业微信工作台，
     * 写死一个会把另一边的用户送到不相干的地方。没有历史记录时才用兜底地址。
     */
    back() {
      if (window.history.length > 1) {
        this.router.back();
      } else {
        this.router.push(this.fallbackPath);
      }
    },
  },
};
